import { RoastResult } from "../schema/roastSchema";
import type { CompanyInputT, RoastResultT } from "../schema/roastSchema";

/**
 * Discriminator for `ApiError`. The SPA state machine only distinguishes
 * between a timeout (the client-side 60 s watchdog fired, or the proxy
 * surfaced a 504) and any other error.
 */
export type ApiErrorKind = "timeout" | "error";

/**
 * Error surface for `requestRoast`. All rejections from the API client that
 * represent a request/response failure are instances of `ApiError`, so the
 * controller can narrow on `kind` without inspecting the underlying cause.
 *
 * Caller-initiated cancellation (via `opts.signal`) is intentionally *not*
 * wrapped — the original `DOMException` / `AbortError` propagates so tests
 * and higher-level code can detect cancellation with normal abort semantics.
 */
export class ApiError extends Error {
  readonly kind: ApiErrorKind;

  constructor(kind: ApiErrorKind, message?: string) {
    super(message ?? kind);
    this.name = "ApiError";
    this.kind = kind;
    // Preserve prototype across transpile targets so `instanceof ApiError`
    // keeps working when this file is compiled down.
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/**
 * Options for {@link requestRoast}. All fields are optional so production
 * callers can use the defaults; tests inject `fetchImpl` and/or `timeoutMs`.
 */
export interface RequestRoastOptions {
  /** Caller-provided abort signal. Aborting this cancels the request. */
  signal?: AbortSignal;
  /** Alternate `fetch` implementation (primarily for tests). */
  fetchImpl?: typeof fetch;
  /** Client-side watchdog in milliseconds. Defaults to 60_000 (AC 2.10). */
  timeoutMs?: number;
  /**
   * API base path. The endpoint is `${apiBase}/roast`. Defaults to `/api`,
   * which `netlify.toml` redirects to `/.netlify/functions/roast-proxy`.
   */
  apiBase?: string;
}

/** Default client-side watchdog (60 s, per AC 2.10). */
const DEFAULT_TIMEOUT_MS = 60_000;

/** Default API base; mapped to the Netlify Function by `netlify.toml`. */
const DEFAULT_API_BASE = "/api";

/**
 * POST a `Company_Input` to the roast proxy and return a validated
 * `Roast_Result`.
 *
 * Behaviour (see design.md — "Netlify Function: roast-proxy" and
 * "Runtime Flow"):
 *
 * - Serializes `input` as JSON and POSTs to `${apiBase}/roast`.
 * - Enforces a client-side watchdog (default 60 s) using an internal
 *   `AbortController`. When the watchdog fires, the promise rejects with
 *   `ApiError('timeout')`.
 * - Forwards caller aborts (`opts.signal`) to the internal controller; a
 *   caller-initiated abort propagates as the native abort error, *not* as
 *   an `ApiError`, so cancellation is distinguishable from a timeout.
 * - On HTTP 504 or non-2xx: rejects with `ApiError('timeout')` or
 *   `ApiError('error')` respectively.
 * - On 2xx: parses JSON, extracts the `roastResult` field, and validates
 *   with the `RoastResult` Zod schema. Validation or parse failures reject
 *   with `ApiError('error')`.
 */
export async function requestRoast(
  input: CompanyInputT,
  opts: RequestRoastOptions = {},
): Promise<RoastResultT> {
  const fetchImpl = opts.fetchImpl ?? globalThis.fetch;
  const timeoutMs = opts.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const apiBase = opts.apiBase ?? DEFAULT_API_BASE;

  if (typeof fetchImpl !== "function") {
    throw new ApiError("error", "fetch implementation is not available");
  }

  const internalCtl = new AbortController();
  // `timedOut` flips to `true` only when the internal watchdog aborts the
  // request. A caller-initiated abort (via `opts.signal`) leaves this
  // `false`, which is how the catch block distinguishes the two cases.
  let timedOut = false;

  const timeoutHandle = setTimeout(() => {
    timedOut = true;
    internalCtl.abort();
  }, timeoutMs);

  // Wire caller abort → internal abort (without flipping `timedOut`).
  const callerSignal = opts.signal;
  let onCallerAbort: (() => void) | undefined;
  if (callerSignal) {
    if (callerSignal.aborted) {
      internalCtl.abort();
    } else {
      onCallerAbort = () => internalCtl.abort();
      callerSignal.addEventListener("abort", onCallerAbort, { once: true });
    }
  }

  const cleanup = (): void => {
    clearTimeout(timeoutHandle);
    if (callerSignal && onCallerAbort) {
      callerSignal.removeEventListener("abort", onCallerAbort);
    }
  };

  let response: Response;
  try {
    response = await fetchImpl(`${apiBase}/roast`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
      signal: internalCtl.signal,
    });
  } catch (err) {
    cleanup();
    if (timedOut) {
      throw new ApiError("timeout");
    }
    // Caller-initiated cancellation — re-raise the original abort error so
    // cancellation can be detected with standard abort semantics.
    if (callerSignal?.aborted) {
      throw err;
    }
    const message = err instanceof Error ? err.message : "network_error";
    throw new ApiError("error", message);
  }

  try {
    if (response.status === 504) {
      throw new ApiError("timeout");
    }
    if (!response.ok) {
      throw new ApiError("error", `HTTP ${response.status}`);
    }

    let body: unknown;
    try {
      body = await response.json();
    } catch {
      throw new ApiError("error", "invalid_json");
    }

    const payload =
      typeof body === "object" && body !== null
        ? (body as { roastResult?: unknown }).roastResult
        : undefined;

    const parsed = RoastResult.safeParse(payload);
    if (!parsed.success) {
      throw new ApiError("error", "schema_invalid");
    }
    return parsed.data;
  } finally {
    cleanup();
  }
}
